import {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type ComponentProps,
} from "react";
import { getIn, useFormik } from "formik";
import * as Yup from "yup";
import Button from "../Button/Button";
import Input from "../Input/Input";
import Dropdown from "../Dropdown/Dropdown";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import CloseIcon from "../../assets/svg/close-icon";
import PlusFilledIcon from "../../assets/svg/plus-filled-icon";
import DragIndicatorIcon from "../../assets/svg/drag-indicator-icon";
import ThreeDotsVerticalIcon from "../../assets/svg/three-dots-vertical-icon";
import { CheckboxChipOption } from "../CheckboxChipOption/CheckboxChipOption";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";

export interface CustomFieldOption {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  iconClass: string;
}

export type CustomFieldCheckboxDisplayStyle = "chips" | "cards";

export interface CustomFieldPayload {
  fieldType: string;
  label: string;
  placeholder: string;
  description: string;
  options: string[];
  isRequired: boolean;
  filterRequired: boolean;
  checkboxDisplayStyle?: CustomFieldCheckboxDisplayStyle;
}

interface CustomFieldFormValues {
  fieldName: string;
  selectedType: string;
  description: string;
  options: { id: string; value: string }[];
  isRequired: boolean;
  filterRequired: boolean;
  checkboxDisplayStyle: CustomFieldCheckboxDisplayStyle;
}

export interface CustomFieldInitialValues {
  fieldName: string;
  fieldType: string;
  placeholder?: string;
  description?: string;
  options?: string[];
  isRequired?: boolean;
  filterRequired?: boolean;
  checkboxDisplayStyle?: CustomFieldCheckboxDisplayStyle;
}

export interface CustomFieldModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldType: string | null;
  fieldOptions: CustomFieldOption[];
  onAddField?: (payload: CustomFieldPayload) => void | Promise<void>;
  title?: string;
  submitText?: string;
  successMessage?: string;
  showFilterRequired?: boolean;
  initialValues?: CustomFieldInitialValues | null;
}

type CustomFieldDragEndEventArg = Parameters<
  NonNullable<ComponentProps<typeof DragDropProvider>["onDragEnd"]>
>[0];

interface SortableDndLike {
  sortable?: { index?: number; initialIndex?: number };
}

function resolveUiFieldType(fieldType: string | null): string | null {
  if (!fieldType) return null;
  if (fieldType === "radio_single_select") return "radio";
  return fieldType;
}

function buildDropdownOptions(fieldOptions: CustomFieldOption[]) {
  return (
    fieldOptions?.map((opt) => {
      const Icon = opt?.icon;
      return {
        value: opt?.id,
        content: (
          <div className="flex items-center gap-2">
            {Icon ? <Icon className={opt?.iconClass} /> : null}
            <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
              {opt?.label}
            </span>
          </div>
        ),
        selectedOptionContent: (
          <div className="flex items-center gap-2">
            {Icon ? <Icon className={opt?.iconClass} /> : null}
            <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
              {opt?.label}
            </span>
          </div>
        ),
      };
    }) ?? []
  );
}

const FIELD_TYPES_WITH_OPTIONS = [
  "dropdown",
  "dropdown-multi",
  "radio",
  "checkbox",
];

const OPTIONS_LABEL_MAP: Record<string, string> = {
  dropdown: "Dropdown Options",
  "dropdown-multi": "Dropdown Options",
  radio: "Radio Options",
  checkbox: "Checkbox Options",
};

const DEFAULT_OPTIONS: { id: string; value: string }[] = [
  { id: "opt-1", value: "" },
  { id: "opt-2", value: "" },
  { id: "opt-3", value: "" },
  { id: "opt-4", value: "" },
];

const DEFAULT_FORM_VALUES: CustomFieldFormValues = {
  fieldName: "",
  selectedType: "",
  description: "",
  options: DEFAULT_OPTIONS,
  isRequired: true,
  filterRequired: true,
  checkboxDisplayStyle: "chips",
};

const customFieldSchema = Yup.object().shape({
  fieldName: Yup.string().trim().required("Enter a field name"),
  selectedType: Yup.string()
    .trim()
    .required("At least one dropdown should be selected"),
  description: Yup.string(),
  options: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string().required(),
        value: Yup.string(),
      })
    )
    .when("selectedType", {
      is: (val: string) => FIELD_TYPES_WITH_OPTIONS.includes(val),
      then: (schema) =>
        schema.test(
          "at-least-one-filled",
          "Add at least one option",
          function (opts) {
            if (
              !!opts &&
              opts.some((o) => (o?.value ?? "").trim().length > 0)
            ) {
              return true;
            }
            return this.createError({
              path: `${this.path}[0].value`,
              message: "Add at least one option",
            });
          }
        ),
    }),
  isRequired: Yup.boolean(),
  filterRequired: Yup.boolean(),
  checkboxDisplayStyle: Yup.string().oneOf(["chips", "cards"]),
});

function PreviewFieldHeader({ title }: { title: string }) {
  return (
    <div className="flex items-center justify-between gap-2 min-w-0">
      <span className="text-md font-medium text-heading-text dark:text-heading-text-dk truncate">
        {title?.trim() ? title : "Field name"}
      </span>
      <span
        className="p-0.5 shrink-0 rounded text-gray-placeholder dark:text-gray-placeholder-dk pointer-events-none"
        aria-hidden
      >
        <ThreeDotsVerticalIcon className="w-4 h-4" />
      </span>
    </div>
  );
}

function RadioPreview({ options }: { options: string[] }) {
  const first = options?.[0] ?? "";
  return (
    <div className="flex flex-col gap-2">
      {options?.map((opt) => (
        <div key={opt} className="flex items-center gap-2.5">
          <input
            type="radio"
            readOnly
            tabIndex={-1}
            checked={opt === first}
            className="shrink-0 w-4 h-4 accent-verba-blue dark:accent-verba-blue-dk pointer-events-none"
            aria-hidden
          />
          <span
            className={`text-sm text-heading-text dark:text-heading-text-dk ${
              opt === first ? "font-semibold" : "font-normal"
            }`}
          >
            {opt}
          </span>
        </div>
      ))}
    </div>
  );
}

function CheckboxChipsPreview({ options }: { options: string[] }) {
  const demoSelected = new Set(options?.slice(0, 1));
  return (
    <div className="flex flex-wrap items-start gap-2">
      {options?.map((opt) => (
        <CheckboxChipOption
          key={opt}
          readOnly
          variant="chip"
          checked={demoSelected.has(opt)}
          label={opt}
        />
      ))}
    </div>
  );
}

function CheckboxCardsPreview({ options }: { options: string[] }) {
  const demoSelected = new Set(options?.slice(0, 1));
  return (
    <div className="grid w-full grid-cols-1 gap-3 min-[400px]:grid-cols-3 items-start">
      {options?.map((opt) => (
        <CheckboxChipOption
          key={opt}
          readOnly
          variant="card"
          checked={demoSelected.has(opt)}
          label={opt}
        />
      ))}
    </div>
  );
}

function SortableOptionItem({
  id,
  index,
  value,
  onChange,
  onRemove,
  canRemove,
  fieldErrorMessage,
  onOptionBlur,
}: {
  id: string;
  index: number;
  value: string;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  fieldErrorMessage?: string;
  onOptionBlur?: () => void;
}) {
  const { ref, handleRef, isDragSource } = useSortable({ id, index });
  const hasFieldError = Boolean(fieldErrorMessage);

  return (
    <div
      ref={ref}
      className={`flex flex-col gap-1 w-full min-w-0 ${isDragSource ? "opacity-50" : ""}`}
    >
      <div
        className={`group flex items-center min-h-9 w-full rounded-lg border bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors focus-within:outline-none ${
          hasFieldError
            ? "border-verba-red-alert dark:border-verba-red-alert-dk focus-within:border-verba-red-alert dark:focus-within:border-verba-red-alert-dk"
            : "border-border-gray dark:border-border-gray-dk focus-within:border-verba-indigo dark:focus-within:border-verba-indigo-dk"
        }`}
      >
        <div
          ref={handleRef}
          className="flex items-center justify-center shrink-0 pl-2.5 pr-1 h-9 cursor-grab active:cursor-grabbing touch-none"
          aria-hidden
        >
          <DragIndicatorIcon className="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(index, e.target?.value ?? "")}
          onBlur={onOptionBlur}
          placeholder={`Option ${index + 1}`}
          aria-invalid={hasFieldError}
          className={`flex-1 min-w-0 h-9 py-0 bg-transparent border-0 text-md text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none ring-0 ${
            canRemove ? "pr-1" : "pr-3"
          }`}
        />
        {canRemove ? (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="group-hover:opacity-100 opacity-0 shrink-0 flex items-center justify-center w-[15px] h-[15px] mr-1.5 rounded-full bg-gray-100 dark:bg-tag-gray-dk cursor-pointer transition-colors hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            aria-label={`Remove option ${index + 1}`}
          >
            <CloseIcon
              className="w-[5px] h-[5px] text-heading-text dark:text-heading-text-dk"
              strokeWidth={1.25}
            />
          </button>
        ) : null}
      </div>
      {fieldErrorMessage ? (
        <div
          role="alert"
          className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk"
        >
          {fieldErrorMessage}
        </div>
      ) : null}
    </div>
  );
}

export function CustomFieldModal({
  open,
  onOpenChange,
  fieldType,
  fieldOptions,
  onAddField,
  title = "Add Custom Field",
  submitText = "Save Changes",
  showFilterRequired = true,
  initialValues,
}: CustomFieldModalProps) {
  const optionIdCounter = useRef(5);
  const optionsScrollRef = useRef<HTMLDivElement>(null);

  const buildInitialValues = useCallback((): CustomFieldFormValues => {
    const resolvedType = resolveUiFieldType(fieldType) ?? fieldType ?? "";
    if (!initialValues) {
      return { ...DEFAULT_FORM_VALUES, selectedType: resolvedType };
    }
    const opts = initialValues?.options?.length
      ? initialValues?.options?.map((val, i) => ({
          id: `opt-${i + 1}`,
          value: val,
        }))
      : DEFAULT_OPTIONS;
    return {
      fieldName: initialValues?.fieldName ?? "",
      selectedType: resolvedType,
      description: initialValues?.description ?? "",
      options: opts,
      isRequired: initialValues?.isRequired ?? true,
      filterRequired: initialValues?.filterRequired ?? true,
      checkboxDisplayStyle:
        initialValues?.checkboxDisplayStyle === "cards" ||
        initialValues?.checkboxDisplayStyle === "chips"
          ? initialValues?.checkboxDisplayStyle
          : "chips",
    };
  }, [fieldType, initialValues]);

  const formik = useFormik<CustomFieldFormValues>({
    initialValues: buildInitialValues(),
    validationSchema: customFieldSchema,
    enableReinitialize: false,
    onSubmit: async (values, { resetForm }) => {
      const trimmed = values?.fieldName?.trim() ?? "";
      const needsOptions = FIELD_TYPES_WITH_OPTIONS.includes(
        values?.selectedType ?? ""
      );

      let payload: CustomFieldPayload;
      if (needsOptions) {
        const filled =
          values?.options?.map((o) => o?.value?.trim())?.filter(Boolean) ?? [];
        payload = {
          fieldType: values?.selectedType ?? "",
          label: trimmed,
          placeholder: "",
          description: values?.description?.trim() ?? "",
          options: filled as string[],
          isRequired: values?.isRequired ?? true,
          filterRequired: values?.filterRequired ?? true,
          ...(values?.selectedType === "checkbox"
            ? { checkboxDisplayStyle: values?.checkboxDisplayStyle }
            : {}),
        };
      } else {
        payload = {
          fieldType: values?.selectedType ?? "",
          label: trimmed,
          placeholder: "",
          description: values?.description?.trim() ?? "",
          options: [],
          isRequired: values?.isRequired ?? true,
          filterRequired: values?.filterRequired ?? true,
        };
      }

      try {
        if (onAddField) {
          await onAddField(payload);
        }
        resetForm();
        onOpenChange(false);
      } catch {
        return;
      }
    },
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    resetForm,
    handleSubmit,
    submitCount,
  } = formik;

  const optionOneValuePath = "options[0].value";
  const optionOneError = getIn(errors, optionOneValuePath) as
    | string
    | undefined;
  const showOptionOneError =
    Boolean(optionOneError) &&
    (Boolean(getIn(touched, optionOneValuePath)) || submitCount > 0);

  const dropdownOptions = useMemo(
    () => buildDropdownOptions(fieldOptions),
    [fieldOptions]
  );

  const showOptions = values?.selectedType
    ? FIELD_TYPES_WITH_OPTIONS.includes(values?.selectedType ?? "")
    : false;

  const optionsSectionLabel = useMemo(
    () =>
      values?.selectedType
        ? (OPTIONS_LABEL_MAP?.[values?.selectedType ?? ""] ?? "Options")
        : "Options",
    [values?.selectedType]
  );

  const previewOptionLabels = useMemo(
    () =>
      values?.options
        ?.map((o) => o?.value?.trim())
        ?.filter(Boolean) as string[],
    [values?.options]
  );

  const showChoicePreview = Boolean(
    showOptions &&
    previewOptionLabels?.length > 0 &&
    (values?.selectedType === "radio" || values?.selectedType === "checkbox")
  );

  useEffect(() => {
    if (open && fieldType) {
      const next = buildInitialValues();
      optionIdCounter.current = (next.options?.length ?? 0) + 1;
      resetForm({ values: next });
    }
  }, [open, fieldType, buildInitialValues, resetForm]);

  const isVisible = Boolean(open && fieldType);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    resetForm({ values: { ...DEFAULT_FORM_VALUES, selectedType: "" } });
  }, [onOpenChange, resetForm]);

  useEffect(() => {
    if (!isVisible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e?.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isVisible, handleClose]);

  const handleAddOption = () => {
    const newId = `opt-${optionIdCounter.current++}`;
    setFieldValue("options", [
      ...(values?.options ?? []),
      { id: newId, value: "" },
    ]);
    setTimeout(() => {
      optionsScrollRef.current?.scrollTo({
        top: optionsScrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 50);
  };

  const handleOptionChange = (index: number, value: string) => {
    const next = [...(values?.options ?? [])];
    next[index] = { ...(next[index] ?? { id: "", value: "" }), value };
    setFieldValue("options", next);
  };

  const handleRemoveOption = (index: number) => {
    if ((values?.options?.length ?? 0) <= 1) return;
    setFieldValue(
      "options",
      values?.options?.filter((_, i) => i !== index) ?? []
    );
  };

  const handleDragEnd = useCallback(
    (event: CustomFieldDragEndEventArg) => {
      const { operation, canceled } = event ?? {};
      if (canceled) return;

      const source = operation?.source;
      if (!source) return;

      const sortableSource = source as SortableDndLike;
      const fromIndex = sortableSource.sortable?.initialIndex;
      const toIndex = sortableSource.sortable?.index;
      if (fromIndex == null || toIndex == null || fromIndex === toIndex) return;

      const next = [...(values?.options ?? [])];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      setFieldValue("options", next);
    },
    [values?.options, setFieldValue]
  );

  if (!isVisible) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 transition-opacity duration-300 ease-out"
        style={{ zIndex: 1000 }}
        aria-hidden
        onClick={handleClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-field-modal-title"
        className="fixed rounded-lg shadow-lg w-[380px] max-h-[95dvh] overflow-auto transition-all duration-300 ease-out transform animate-modal-enter bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk border border-border-gray dark:border-border-gray-dk"
        style={{
          zIndex: 1001,
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-0">
          <div
            className="flex flex-col w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 pt-5 pb-4">
              <h2
                id="custom-field-modal-title"
                className="text-base font-medium text-heading-text dark:text-heading-text-dk"
              >
                {title}
              </h2>
              <button
                type="button"
                className="p-[5px] shrink-0 bg-gray-100 dark:bg-tag-gray-dk rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110 cursor-pointer"
                onClick={handleClose}
                aria-label="Close"
              >
                <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
              </button>
            </div>
            <div className="px-6 flex flex-col gap-5 max-h-[min(50vh,400px)]">
              <Input
                label="Field Name"
                name="fieldName"
                id="custom-field-name"
                type="text"
                placeholder="e.g, Emergency Contact"
                value={values?.fieldName ?? ""}
                onChange={(e) =>
                  setFieldValue("fieldName", e.target?.value ?? "")
                }
                onBlur={() => formik.setFieldTouched("fieldName", true)}
                error={!!(touched?.fieldName && errors?.fieldName)}
                errorMessage={errors?.fieldName}
                secondaryContainerClassName={`rounded-lg border ${
                  touched?.fieldName && errors?.fieldName
                    ? "border-verba-red-alert dark:border-verba-red-alert-dk"
                    : "border-border-gray dark:border-border-gray-dk focus-within:border-verba-indigo dark:focus-within:border-verba-indigo-dk"
                } p-2 bg-pure-white dark:bg-pure-white-dk transition-colors`}
                labelClassName="text-md font-medium text-heading-text dark:text-heading-text-dk"
              />

              <div className="flex flex-col gap-1">
                <Dropdown
                  id="custom-field-type-dropdown"
                  label="Field type"
                  options={dropdownOptions}
                  value={values?.selectedType ?? ""}
                  onChange={(val) => {
                    setFieldValue("selectedType", val as string);
                    formik.setFieldTouched("selectedType", true);
                  }}
                  placeholder="Select field type"
                  ariaLabel="Field type"
                  className="w-full"
                  triggerClassName={`!bg-pure-white dark:!bg-pure-white-dk !rounded-lg ${
                    touched?.selectedType && errors?.selectedType
                      ? "!border-verba-red-alert dark:!border-verba-red-alert-dk"
                      : ""
                  }`}
                  selectedOptionContainerClassName="h-[36px] w-full"
                  labelClassName="text-md font-medium text-heading-text dark:text-heading-text-dk"
                  placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
                  dropdownClassName="!max-h-[200px] mt-1"
                />
                {touched?.selectedType && errors?.selectedType && (
                  <div
                    role="alert"
                    className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk"
                  >
                    {errors?.selectedType}
                  </div>
                )}
              </div>

              <Input
                label={
                  <>
                    Description{" "}
                    <span className="text-sm font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
                      (Optional)
                    </span>
                  </>
                }
                name="custom-field-description"
                id="custom-field-description"
                type="text"
                placeholder="What information should this field capture?"
                value={formik.values?.description ?? ""}
                onChange={(e) =>
                  formik.setFieldValue("description", e.target?.value ?? "")
                }
                containerClassName="w-full"
                secondaryContainerClassName="box-border h-[36px] min-h-[36px] max-h-[36px] shrink-0 overflow-hidden rounded-lg border border-border-gray dark:border-border-gray-dk px-2 py-0 focus-within:border-verba-indigo dark:focus-within:border-verba-indigo-dk bg-pure-white dark:bg-pure-white-dk transition-colors"
                labelClassName="text-md font-medium text-heading-text dark:text-heading-text-dk"
              />

              {showOptions && (
                <div
                  ref={optionsScrollRef}
                  className="flex flex-col gap-2 max-h-[min(220px,40vh)] overflow-y-auto overflow-x-hidden"
                >
                  <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
                    {optionsSectionLabel}
                  </span>
                  <div className="pr-1 flex flex-col gap-2">
                    <DragDropProvider onDragEnd={handleDragEnd}>
                      <div className="flex flex-col gap-2">
                        {formik?.values?.options?.map((opt, index) => (
                          <SortableOptionItem
                            key={opt?.id}
                            id={opt?.id ?? ""}
                            index={index}
                            value={opt?.value ?? ""}
                            onChange={handleOptionChange}
                            onRemove={handleRemoveOption}
                            canRemove={
                              (formik?.values?.options?.length ?? 0) > 1
                            }
                            fieldErrorMessage={
                              index === 0 && showOptionOneError
                                ? optionOneError
                                : undefined
                            }
                            onOptionBlur={
                              index === 0
                                ? () =>
                                    setFieldTouched(optionOneValuePath, true)
                                : undefined
                            }
                          />
                        ))}
                      </div>
                    </DragDropProvider>
                  </div>
                </div>
              )}
            </div>

            {showOptions && (
              <button
                type="button"
                onClick={handleAddOption}
                className="flex items-center gap-1 w-fit cursor-pointer shrink-0 pl-6 pt-3"
              >
                <PlusFilledIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
                <span className="text-sm font-medium text-verba-blue dark:text-verba-blue-dk">
                  Add More
                </span>
              </button>
            )}

            {showFilterRequired && (
              <div className="flex flex-col gap-3 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-md font-semibold text-heading-text dark:text-heading-text-dk">
                      Required Field
                    </span>
                    <span className="text-xs font-normal text-data dark:text-data-dk leading-normal">
                      Mandatory that the users must fill this field
                    </span>
                  </div>
                  <ToggleSwitch
                    isChecked={values?.isRequired ?? true}
                    handleToggle={() =>
                      setFieldValue("isRequired", !values?.isRequired)
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-md font-semibold text-heading-text dark:text-heading-text-dk">
                      Filter Required
                    </span>
                    <span className="text-xs font-normal text-data dark:text-data-dk leading-normal">
                      Add a filter for this field in the Filters panel.
                    </span>
                  </div>
                  <ToggleSwitch
                    isChecked={values?.filterRequired ?? true}
                    handleToggle={() =>
                      setFieldValue("filterRequired", !values?.filterRequired)
                    }
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-3 px-6 py-4">
              <Button
                type="button"
                innerText="Cancel"
                onClick={handleClose}
                buttonContainerClass="min-w-[120px] py-1 rounded-md border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                innerTextClass="text-md font-semibold text-heading-text dark:text-heading-text-dk"
              />
              <Button
                type="button"
                innerText={submitText}
                onClick={() => handleSubmit()}
                buttonContainerClass="min-w-[140px] py-1 rounded-md bg-verba-indigo dark:bg-verba-indigo-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk"
                innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
